import { useState } from 'react';
import type { FormEvent } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { PageHeader } from '@/components/PageHeader';
import { PhotoGrid } from '@/components/PhotoGrid';
import { QueryState } from '@/components/QueryState';
import { NcrBadge } from '@/components/StatusBadge';
import { shortDate } from '@/lib/format';
import { keys, useNcrActions, useNcrs, useSiteVendors } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { Ncr, Trade } from '@/lib/types';

const TRADES: Trade[] = ['건축', '구조', '기계', '전기', '통신', '토목', '조경', '기타'];

/**
 * NCR(부적합) · 시정조치.
 *
 * <p>검측의 「불합격」과 다르다 — 검측은 <b>그 회차를 다시 보는</b> 것으로 끝나지만, 부적합은
 * 원인과 재발방지를 적어 닫는다. 그래서 상태가 하나 더 있다(`조치확인` → `종결`): 회신을
 * 확인한 것과 그 부적합이 닫힌 것은 다르다.
 *
 * <p>발행과 종결은 판정하는 손(감리·직원)이, 원인·시정조치·재발방지 회신은 협력사가 한다.
 * <b>재발방지가 빈 회신은 받지 않는다</b> — 그것이 없으면 같은 부적합이 다음 층에서 그대로 난다.
 * 사진은 증빙을 만드는 손(협력사·직원)이 발행 상태에서 붙인다(DOC-87 §3-4).
 */
export function NcrPage() {
  const { siteId, siteName, isJudge, isVendor, canSubmitEvidence } = useSite();
  const queryClient = useQueryClient();
  const ncrs = useNcrs(siteId);
  const vendors = useSiteVendors(siteId);
  const { issue, respond, review } = useNcrActions(siteId);

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [trade, setTrade] = useState<Trade>('건축');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  /** 수신 업체. 비면 부적합이 어느 협력사 화면에도 뜨지 않는다 — 그래서 필수다. */
  const [vendorId, setVendorId] = useState('');

  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [cause, setCause] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [preventiveAction, setPreventiveAction] = useState('');

  const rows = ncrs.data ?? [];
  const vendorRows = vendors.data ?? [];

  async function onIssue(event: FormEvent) {
    event.preventDefault();
    await issue.mutateAsync({
      title: title.trim(),
      trade,
      description: description.trim(),
      dueDate: dueDate === '' ? null : dueDate,
      vendorId,
    });
    setOpen(false);
    setTitle('');
    setDescription('');
    setDueDate('');
    setVendorId('');
  }

  async function onRespond(event: FormEvent, id: string) {
    event.preventDefault();
    await respond.mutateAsync({
      id,
      cause: cause.trim(),
      correctiveAction: correctiveAction.trim(),
      preventiveAction: preventiveAction.trim(),
    });
    setRespondingTo(null);
    setCause('');
    setCorrectiveAction('');
    setPreventiveAction('');
  }

  return (
    <>
      <PageHeader
        title="부적합"
        subtitle={siteName}
        action={
          isJudge && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpen(true)}>
              발행
            </button>
          )
        }
      />

      {open && (
        <form onSubmit={onIssue} className="card mb-3">
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>수신 업체</span>
            <select className="input-field" value={vendorId} onChange={(e) => setVendorId(e.target.value)} required>
              <option value="">선택</option>
              {vendorRows.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.mainTrade === null ? it.name : `${it.name} · ${it.mainTrade}`}
                </option>
              ))}
            </select>
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>제목</span>
            <input type="text" className="input-field" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>부적합 내용</span>
            <textarea className="input-field" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} required />
          </label>
          <div className="mb-4 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>공종</span>
              <select className="input-field" value={trade} onChange={(e) => setTrade(e.target.value as Trade)}>
                {TRADES.map((it) => <option key={it} value={it}>{it}</option>)}
              </select>
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>회신 기한</span>
              <input type="date" className="input-field" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={issue.isPending || vendorId === ''}>
              {issue.isPending ? '보내는 중입니다…' : '발행'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>취소</button>
          </div>
        </form>
      )}

      <QueryState
        pending={ncrs.isPending}
        error={ncrs.isError}
        empty={rows.length === 0}
        emptyText="발행된 부적합이 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="card">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-ui-md font-medium">{row.title}</div>
                  <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                    {row.ncrNo} · {row.trade ?? '공종 미지정'} · {shortDate(row.issuedOn)} 발행
                    {row.dueDate !== null && ` · 회신 기한 ${shortDate(row.dueDate)}`}
                  </div>
                </div>
                <NcrBadge status={row.status} />
              </div>

              <p className="text-ui-sm">{row.description}</p>

              {row.respondedOn !== null && (
                <dl className="mt-3 flex flex-col gap-2 rounded-md p-3 text-ui-sm" style={{ background: 'var(--bg-hover)' }}>
                  <Field label="원인" value={row.cause} />
                  <Field label="시정조치" value={row.correctiveAction} />
                  <Field label="재발방지" value={row.preventiveAction} />
                </dl>
              )}

              {/*
                조치 사진 — 회신에 붙는다(DOC-84 §3-D-4). 협력사·직원이 발행 상태에서만 붙이고, 회신이 나간
                뒤에는 감리가 본다. 사진이 없고 붙일 수도 없으면 칸을 세우지 않는다.
              */}
              {(row.photoCount > 0 || (canSubmitEvidence && row.status === '발행')) && (
                <div className="mt-3">
                  <PhotoGrid
                    target="ncrs"
                    parentId={row.id}
                    canUpload={canSubmitEvidence && row.status === '발행'}
                    onUploaded={() => void queryClient.invalidateQueries({ queryKey: keys.ncrs(siteId) })}
                  />
                </div>
              )}

              {isVendor && row.status === '발행' && (
                respondingTo === row.id ? (
                  <form onSubmit={(e) => onRespond(e, row.id)} className="mt-3">
                    <label className="mb-2 block">
                      <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>원인</span>
                      <textarea className="input-field" rows={2} value={cause} onChange={(e) => setCause(e.target.value)} required />
                    </label>
                    <label className="mb-2 block">
                      <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>시정조치</span>
                      <textarea className="input-field" rows={2} value={correctiveAction} onChange={(e) => setCorrectiveAction(e.target.value)} required />
                    </label>
                    <label className="mb-3 block">
                      <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>재발방지</span>
                      <textarea className="input-field" rows={2} value={preventiveAction} onChange={(e) => setPreventiveAction(e.target.value)} required />
                    </label>
                    <div className="flex gap-2">
                      <button type="submit" className="btn-primary flex-1" disabled={respond.isPending}>
                        {respond.isPending ? '보내는 중입니다…' : '회신'}
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => setRespondingTo(null)}>취소</button>
                    </div>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="btn-secondary mt-3 w-full"
                    onClick={() => { setRespondingTo(row.id); setCause(''); setCorrectiveAction(''); setPreventiveAction(''); }}
                  >
                    조치 회신
                  </button>
                )
              )}

              {isJudge && row.status === '조치제출' && (
                <button
                  type="button"
                  className="btn-primary mt-3 w-full"
                  onClick={() => review.mutate({ id: row.id, status: '조치확인' })}
                  disabled={review.isPending}
                >
                  조치 확인
                </button>
              )}

              {isJudge && row.status === '조치확인' && (
                <button
                  type="button"
                  className="btn-secondary mt-3 w-full"
                  onClick={() => review.mutate({ id: row.id, status: '종결' })}
                  disabled={review.isPending}
                >
                  종결
                </button>
              )}

              {row.closedOn !== null && (
                <div className="mt-2 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                  {shortDate(row.closedOn)} 종결
                </div>
              )}
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}

function Field({ label, value }: { label: string; value: Ncr['cause'] }) {
  if (value === null) return null;
  return (
    <div>
      <dt className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
